import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import { monitoringService } from "@/services/monitoring.service";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const TREND_DAYS = [7, 14, 30, 60, 90] as const;

export default function UsageAnalyticsPage() {
  const [trendDays, setTrendDays] = useState(30);

  // Engagement (DAU/WAU/MAU)
  const { data: engagement } = useQuery({
    queryKey: ["analytics", "engagement"],
    queryFn: () => monitoringService.getEngagement(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Usage trends
  const { data: trends } = useQuery({
    queryKey: ["analytics", "usage-trends", trendDays],
    queryFn: () => monitoringService.getUsageTrends(trendDays),
  });

  // Platform distribution
  const { data: platforms } = useQuery({
    queryKey: ["analytics", "platform-distribution"],
    queryFn: () => monitoringService.getPlatformDistribution(30),
  });

  // Top features
  const { data: features } = useQuery({
    queryKey: ["analytics", "top-features"],
    queryFn: () => monitoringService.getTopFeatures(30, 15),
  });

  // Cohort analysis
  const { data: cohorts } = useQuery({
    queryKey: ["analytics", "cohort-analysis"],
    queryFn: () => monitoringService.getCohortAnalysis(6),
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Usage Analytics"
        subtitle="Platform-wide engagement, trends, and feature adoption"
      />

      {/* Engagement cards */}
      {engagement && (
        <div className="grid gap-4 sm:grid-cols-3">
          <EngagementCard
            label="DAU"
            value={engagement.dau}
            description="Daily Active"
          />
          <EngagementCard
            label="WAU"
            value={engagement.wau}
            description="Weekly Active"
          />
          <EngagementCard
            label="MAU"
            value={engagement.mau}
            description="Monthly Active"
          />
        </div>
      )}

      {engagement && (
        <div className="grid gap-4 sm:grid-cols-2">
          <RatioCard
            label="DAU/MAU Ratio"
            value={((engagement.dauMauRatio ?? 0) * 100).toFixed(1) + "%"}
            description="Stickiness — higher is better (>20% = good)"
            color={
              (engagement.dauMauRatio ?? 0) > 0.2
                ? "text-green-600"
                : "text-amber-600"
            }
          />
          <RatioCard
            label="DAU/WAU Ratio"
            value={((engagement.dauWauRatio ?? 0) * 100).toFixed(1) + "%"}
            description="Weekly stickiness — higher is better (>40% = good)"
            color={
              (engagement.dauWauRatio ?? 0) > 0.4
                ? "text-green-600"
                : "text-amber-600"
            }
          />
        </div>
      )}

      {/* 14-day engagement trend (sparkline table) */}
      {engagement?.trend && engagement.trend.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            14-Day Active Tenant Trend
          </h2>
          <div className="mt-4 flex items-end gap-1" style={{ height: 80 }}>
            {(() => {
              const max = Math.max(
                ...engagement.trend.map((d: { active: number }) => d.active),
                1,
              );
              return engagement.trend.map(
                (d: { date: string; active: number }, i: number) => (
                  <div
                    key={d.date}
                    title={`${d.date}: ${d.active} active`}
                    className="flex-1 rounded-t bg-blue-500 transition-all hover:bg-blue-600"
                    style={{
                      height: `${(d.active / max) * 100}%`,
                      minHeight: 2,
                      opacity: 0.5 + (i / engagement.trend.length) * 0.5,
                    }}
                  />
                ),
              );
            })()}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-slate-400">
            <span>{engagement.trend[0]?.date}</span>
            <span>{engagement.trend[engagement.trend.length - 1]?.date}</span>
          </div>
        </section>
      )}

      {/* Usage trends table */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Usage Trends</h2>
          <select
            value={trendDays}
            onChange={(e) => setTrendDays(Number(e.target.value))}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
          >
            {TREND_DAYS.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </div>
        {trends && trends.length > 0 ? (
          <div className="mt-4 max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-left text-slate-400">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium text-right">Active</th>
                  <th className="pb-2 font-medium text-right">Logins</th>
                  <th className="pb-2 font-medium text-right">Invoices</th>
                  <th className="pb-2 font-medium text-right">Avg Health</th>
                </tr>
              </thead>
              <tbody>
                {trends.map(
                  (d: {
                    date: string;
                    activeTenants: number;
                    logins: number;
                    invoices: number;
                    avgHealthScore: number;
                  }) => (
                    <tr
                      key={d.date}
                      className="border-b border-slate-50 text-slate-700"
                    >
                      <td className="py-1.5">{d.date}</td>
                      <td className="py-1.5 text-right">{d.activeTenants}</td>
                      <td className="py-1.5 text-right">{d.logins}</td>
                      <td className="py-1.5 text-right">{d.invoices}</td>
                      <td className="py-1.5 text-right">
                        {(d.avgHealthScore ?? 0).toFixed(0)}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-400">No trend data yet.</p>
        )}
      </section>

      {/* Platform distribution + Top features side-by-side */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Platform distribution */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Platform Distribution
          </h2>
          {platforms && platforms.length > 0 ? (
            <div className="mt-4 space-y-3">
              {platforms.map(
                (p: {
                  platform: string;
                  sessions: number;
                  percentage: number;
                }) => (
                  <div key={p.platform}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium capitalize text-slate-700">
                        {p.platform}
                      </span>
                      <span className="text-slate-400">
                        {p.sessions} sessions ({p.percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-blue-500"
                        style={{ width: `${p.percentage}%` }}
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          ) : (
            <p className="mt-4 text-xs text-slate-400">No platform data yet.</p>
          )}
        </section>

        {/* Top features */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Top Features (Last 30d)
          </h2>
          {features && features.length > 0 ? (
            <div className="mt-4 space-y-2">
              {(() => {
                const maxCount = features[0]?.count ?? 1;
                return features.map(
                  (f: { feature: string; count: number }, i: number) => (
                    <div key={f.feature} className="flex items-center gap-3">
                      <span className="w-4 shrink-0 text-right text-[10px] text-slate-400">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="truncate font-medium text-slate-700">
                            {f.feature}
                          </span>
                          <span className="shrink-0 text-slate-400">
                            {f.count}
                          </span>
                        </div>
                        <div className="mt-0.5 h-1 rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-violet-500"
                            style={{
                              width: `${(f.count / maxCount) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ),
                );
              })()}
            </div>
          ) : (
            <p className="mt-4 text-xs text-slate-400">No feature data yet.</p>
          )}
        </section>
      </div>

      {/* Cohort retention matrix */}
      {cohorts && cohorts.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Cohort Retention
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Percentage of signup cohort still active in subsequent months
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr className="text-left text-slate-400">
                  <th className="pr-4 pb-2 font-medium">Cohort</th>
                  <th className="pr-4 pb-2 font-medium text-right">Size</th>
                  {cohorts[0]?.retention?.map((_: unknown, i: number) => (
                    <th key={i} className="pr-3 pb-2 font-medium text-right">
                      M{i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map(
                  (c: { month: string; size: number; retention: number[] }) => (
                    <tr key={c.month} className="border-t border-slate-50">
                      <td className="pr-4 py-1.5 font-medium text-slate-700">
                        {c.month}
                      </td>
                      <td className="pr-4 py-1.5 text-right text-slate-500">
                        {c.size}
                      </td>
                      {c.retention.map((r: number, i: number) => (
                        <td
                          key={i}
                          className="pr-3 py-1.5 text-right"
                          style={{
                            color:
                              r > 70
                                ? "#16a34a"
                                : r > 40
                                  ? "#d97706"
                                  : "#dc2626",
                          }}
                        >
                          {r.toFixed(0)}%
                        </td>
                      ))}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function EngagementCard({
  label,
  value,
  description,
}: {
  label: string;
  value: number;
  description: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-400">{description}</p>
    </div>
  );
}

function RatioCard({
  label,
  value,
  description,
  color,
}: {
  label: string;
  value: string;
  description: string;
  color: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </p>
      <p className={`mt-2 text-3xl font-semibold ${color}`}>{value}</p>
      <p className="mt-1 text-xs text-slate-400">{description}</p>
    </div>
  );
}
